import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import MouseTrail from '../components/MouseTrail';
import TextScramble from '../components/TextScramble';
import { ExternalLink } from 'lucide-react';
import { portfolioAPI } from '../services/api';
import { useScrollReveal } from '../hooks/useScrollReveal';
import GlowCard from '../components/GlowCard';
import { useTheme } from '../context/ThemeContext';

const ScrollRevealSection = ({ children, className = '', delay = 0 }) => {
  const ref = useScrollReveal({ threshold: 0.1, triggerOnce: true });
  
  return (
    <div 
      ref={ref} 
      className={`scroll-reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Portfolio = () => {
  const [filter, setFilter] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();
  
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await portfolioAPI.getAll(filter === 'All' ? null : filter);
        setProjects(response.data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [filter]);
  
  const categories = ['All', 'Web Application', 'Enterprise Software', 'Mobile Application', 'Data Visualization', 'IoT Solution'];
  
  const filteredProjects = projects;

  const glowColors = isDarkMode 
    ? ['rgba(138, 100, 255, 0.6)', 'rgba(100, 200, 255, 0.6)', 'rgba(255, 150, 100, 0.6)', 'rgba(100, 255, 180, 0.6)', 'rgba(255, 100, 150, 0.6)', 'rgba(200, 200, 100, 0.6)']
    : ['rgba(99, 102, 241, 0.3)', 'rgba(56, 189, 248, 0.3)', 'rgba(251, 146, 60, 0.3)', 'rgba(34, 197, 94, 0.3)', 'rgba(244, 63, 94, 0.3)', 'rgba(234, 179, 8, 0.3)'];

  return (
    <div className={`relative min-h-screen ${isDarkMode ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <MouseTrail />
      <Navigation />
      
      <main className="relative z-10 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-16">
            <h1 className={`text-6xl md:text-8xl font-light leading-tight mb-6 tracking-tight ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <TextScramble as="span" speed={30}>Our Work</TextScramble>
            </h1>
            <p className={`text-xl font-light ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <TextScramble as="span" speed={20}>Showcasing our latest projects and digital experiences</TextScramble>
            </p>
          </div>

          <div className="flex flex-wrap gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-5 py-2 text-sm border transition-all duration-300 ${
                  filter === category
                    ? isDarkMode 
                      ? 'border-white bg-white text-black'
                      : 'border-gray-900 bg-gray-900 text-white'
                    : isDarkMode
                      ? 'border-white/20 text-gray-400 hover:border-white/60 hover:text-white'
                      : 'border-gray-300 text-gray-500 hover:border-gray-500 hover:text-gray-900'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredProjects.map((project, index) => (
              <ScrollRevealSection key={project.id} delay={index * 100}>
                <GlowCard 
                  className={`group relative overflow-hidden border backdrop-blur-sm transition-all duration-500 ${
                    isDarkMode 
                      ? 'border-white/10 bg-black/30' 
                      : 'border-gray-200 bg-white/80 shadow-lg'
                  }`}
                  glowColor={glowColors[index % glowColors.length]}
                  glowSize={300}
                >
                  <div className={`aspect-[4/3] overflow-hidden relative ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {project.project_url ? (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center ${
                          isDarkMode ? 'bg-black/60' : 'bg-white/80'
                        }`}
                      >
                        <div className="text-center">
                          <ExternalLink className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
                          <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>View Project</span>
                        </div>
                      </a>
                    ) : (
                      <div className={`absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center ${
                        isDarkMode ? 'bg-black/60' : 'bg-white/80'
                      }`}>
                        <ExternalLink className={`w-8 h-8 ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className={`text-2xl font-light transition-colors duration-300 ${
                        isDarkMode ? 'group-hover:text-white' : 'text-gray-900'
                      }`}>
                        <TextScramble as="span" speed={30}>{project.title}</TextScramble>
                      </h3>
                    </div>
                    
                    <p className={`text-sm leading-relaxed mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <TextScramble as="span" speed={20}>{project.description}</TextScramble>
                    </p>
                    
                    <div className="flex flex-wrap gap-2">
                      {project.tech.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className={`text-xs px-3 py-1 border transition-colors duration-300 ${
                            isDarkMode 
                              ? 'border-white/10 text-gray-400 group-hover:border-white/30 group-hover:text-white'
                              : 'border-gray-200 text-gray-500 group-hover:border-gray-400 group-hover:text-gray-900'
                          }`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </GlowCard>
              </ScrollRevealSection>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Portfolio;
